import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api, Task, Contact } from '../api/client';

export default function Tasks() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState<'all' | 'today' | 'overdue' | 'upcoming'>('today');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', dueDate: '', contactId: '' });

  // Estado para el autocomplete de contactos
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState<Contact[]>([]);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    loadTasks();
  }, [filter]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const params: any = {};

      if (filter === 'today') params.due = 'today';
      else if (filter === 'upcoming') params.due = 'upcoming';
      else if (filter === 'overdue') params.due = 'overdue';

      const response = await api.tasks.list(params);
      setTasks(response.data);
    } catch (err) {
      console.error('Error loading tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (id: number) => {
    try {
      await api.tasks.complete(id);
      setTasks(tasks.map(t => t.id === id ? { ...t, completed: 1 } : t));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al completar tarea');
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return; // Prevenir clics múltiples
    setSubmitting(true);
    try {
      await api.tasks.create({
        title: form.title,
        dueDate: form.dueDate || undefined,
        contactId: form.contactId ? parseInt(form.contactId) : undefined,
      });
      setShowForm(false);
      setForm({ title: '', dueDate: '', contactId: '' });
      setContactSearch('');
      setSelectedContact(null);
      loadTasks();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al crear tarea');
    } finally {
      setSubmitting(false);
    }
  };

  // Buscar contactos por nombre
  const searchContacts = async (query: string) => {
    if (query.length < 1) {
      setContactResults([]);
      setShowDropdown(false);
      return;
    }

    try {
      const response = await api.contacts.list({ search: query });
      setContactResults(response.data);
      setShowDropdown(true);
    } catch (err) {
      console.error('Error searching contacts:', err);
    }
  };

  // Manejar cambio en el input de búsqueda
  const handleContactSearchChange = (value: string) => {
    setContactSearch(value);
    setSelectedContact(null);
    setForm({ ...form, contactId: '' });

    // Debounce: esperar 300ms después de que el usuario deje de escribir
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      searchContacts(value);
    }, 300);
  };

  // Seleccionar un contacto del dropdown
  const handleSelectContact = (contact: Contact) => {
    setSelectedContact(contact);
    setContactSearch(contact.name);
    setForm({ ...form, contactId: contact.id.toString() });
    setShowDropdown(false);
  };

  // Limpiar selección de contacto
  const handleClearContact = () => {
    setSelectedContact(null);
    setContactSearch('');
    setForm({ ...form, contactId: '' });
    setShowDropdown(false);
  };

  const pendingTasks = tasks.filter(t => t.completed === 0);
  const completedTasks = tasks.filter(t => t.completed === 1);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">✅ Tareas</h1>
        <button
          onClick={() => setShowForm(true)}
          className="bg-amazon-orange text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition font-medium text-sm sm:text-base"
        >
          ➕ Nueva Tarea
        </button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-2">
        {(['today', 'overdue', 'upcoming', 'all'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              filter === f
                ? 'bg-amazon-orange text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f === 'today' && '📅 Hoy'}
            {f === 'overdue' && '⚠️ Vencidas'}
            {f === 'upcoming' && '📆 Próximas'}
            {f === 'all' && '📋 Todas'}
          </button>
        ))}
      </div>

      {/* Formulario de nueva tarea */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
          <h2 className="text-lg font-semibold mb-4">Nueva Tarea</h2>
          <form onSubmit={handleCreate} className="space-y-4">
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Título de la tarea"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amazon-orange focus:border-transparent outline-none"
              required
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input
                type="date"
                value={form.dueDate}
                onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amazon-orange focus:border-transparent outline-none"
              />

              {/* Autocomplete de contactos */}
              <div className="relative" ref={dropdownRef}>
                <div className="relative">
                  <input
                    type="text"
                    value={contactSearch}
                    onChange={(e) => handleContactSearchChange(e.target.value)}
                    onFocus={() => contactSearch.length > 0 && contactResults.length > 0 && setShowDropdown(true)}
                    placeholder="🔍 Buscar contacto por nombre..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amazon-orange focus:border-transparent outline-none pr-10"
                  />
                  {selectedContact && (
                    <button
                      type="button"
                      onClick={handleClearContact}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* Dropdown de resultados */}
                {showDropdown && contactResults.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {contactResults.map((contact) => (
                      <button
                        key={contact.id}
                        type="button"
                        onClick={() => handleSelectContact(contact)}
                        className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 border-b border-gray-100 last:border-0"
                      >
                        <div className="w-8 h-8 bg-amazon-orange rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                          {contact.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-800 truncate">{contact.name}</p>
                          <p className="text-sm text-gray-500 truncate">
                            {contact.company || contact.email || 'Sin información'}
                          </p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full flex-shrink-0 ${
                          contact.status === 'lead' ? 'bg-blue-100 text-blue-800' :
                          contact.status === 'contactado' ? 'bg-yellow-100 text-yellow-800' :
                          contact.status === 'en_negociacion' ? 'bg-orange-100 text-orange-800' :
                          contact.status === 'cliente' ? 'bg-green-100 text-green-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {contact.status}
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Mensaje cuando no hay resultados */}
                {showDropdown && contactSearch.length > 0 && contactResults.length === 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500">
                    No se encontraron contactos
                  </div>
                )}
              </div>
            </div>

            {/* Contacto seleccionado */}
            {selectedContact && (
              <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg text-sm">
                <span className="text-green-600">✓</span>
                <span className="text-green-800">
                  Contacto: <strong>{selectedContact.name}</strong>
                  {selectedContact.company && ` (${selectedContact.company})`}
                </span>
              </div>
            )}

            <div className="flex gap-2">
              <button 
                type="submit" 
                disabled={submitting}
                className="bg-amazon-orange text-white px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Guardando...
                  </>
                ) : (
                  'Guardar'
                )}
              </button>
              <button type="button" onClick={() => { setShowForm(false); handleClearContact(); }} className="text-gray-500 px-4 py-2">
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amazon-orange"></div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Tareas pendientes */}
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
            <h2 className="text-lg font-semibold mb-4">
              📋 Pendientes ({pendingTasks.length})
            </h2>
            <div className="space-y-3">
              {pendingTasks.length === 0 ? (
                <p className="text-gray-500 text-center py-4">
                  No hay tareas pendientes 🎉
                </p>
              ) : (
                pendingTasks.map((task) => (
                  <div key={task.id} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                    <button
                      onClick={() => handleComplete(task.id)}
                      className="mt-1 text-gray-400 hover:text-green-500 transition"
                      title="Marcar como completada"
                    >
                      ☐
                    </button>
                    <div className="flex-1">
                      <p className="font-medium">{task.title}</p>
                      {task.contact_name && (
                        <Link
                          to={`/contacts/${task.contact_id}`}
                          className="text-sm text-amazon-orange hover:underline"
                        >
                          → {task.contact_name}
                        </Link>
                      )}
                    </div>
                    {task.due_date && (
                      <span className={`text-xs px-2 py-1 rounded ${
                        new Date(task.due_date) < new Date()
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-200 text-gray-600'
                      }`}>
                        {task.due_date}
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Tareas completadas */}
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
            <h2 className="text-lg font-semibold mb-4">
              ✅ Completadas ({completedTasks.length})
            </h2>
            <div className="space-y-3">
              {completedTasks.length === 0 ? (
                <p className="text-gray-500 text-center py-4">
                  No hay tareas completadas
                </p>
              ) : (
                completedTasks.map((task) => (
                  <div key={task.id} className="flex items-start gap-3 p-4 bg-green-50 rounded-lg opacity-75">
                    <span className="mt-1 text-green-500">☑</span>
                    <div className="flex-1">
                      <p className="font-medium line-through">{task.title}</p>
                      {task.contact_name && (
                        <span className="text-sm text-gray-500">
                          → {task.contact_name}
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
